import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { DialogContent, SvgIcon } from '@mui/material'

import Dialog from '@mui/material/Dialog'
import React from 'react'
import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'
import { DonutChartData, DonutLegendItem } from '../../../../shared/components/DonutCharts'
import Link from '@mui/material/Link'
import { OverflowTooltipWrapper } from '../../../../shared/components/OverflowTooltipWrapper/OverflowTooltipWrapper'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { ConditionalWrapper } from '../../../../shared/components/ConditionalWrapper'
import { toDisplayValue } from '../../../../shared/utils/displayValue'

type ListProps<T> = {
    data: DonutChartData<T>
    legend: DonutLegendItem[]
    handleClose: () => void
    getName: (item: T) => string
    getPathname?: (item: T) => string
}
const List = <T,>({ data, handleClose, getName, getPathname, legend }: ListProps<T>) => {
    const history = useHistory()

    if (!data) return null

    const total = data.values.reduce((acc, item) => acc + item.count, 0)

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            aria-labelledby="form-dialog-title"
            maxWidth="md"
            PaperProps={{
                sx: {
                    py: 4,
                    px: 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Typography variant="22x600" color="figma.grayscale.textHier1">
                            {data.title}
                        </Typography>
                        <Typography variant="16x600" color="figma.grayscale.textHier2">
                            {total} count
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={1} pt={2} pb={1}>
                    {data.values.map((value) => {
                        if (!value.items.length) return

                        const legendItem = legend.find((item) => item.label === value.label)

                        return (
                            <Stack direction="column" gap={0.5} py={1}>
                                <Stack
                                    direction="row"
                                    gap={1}
                                    alignItems="center"
                                    position="sticky"
                                    top={0}
                                    py={1}
                                    my={-1}
                                    zIndex={1}
                                    bgcolor="background.paper"
                                >
                                    <SvgIcon
                                        component={legendItem.icon}
                                        inheritViewBox
                                        fontSize="small"
                                        style={{
                                            fontSize: '14px',
                                            color: legendItem.color,
                                        }}
                                    />
                                    <Stack direction="row" alignItems="center" gap={1.5}>
                                        <Typography variant="16x500" color="figma.grayscale.textHier1">
                                            {legendItem.label}
                                        </Typography>
                                        <Typography variant="14x500" color="figma.grayscale.textHier2">
                                            {value.items.length} count
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction="column" gap={0.5} py={1}>
                                    {value.items.map((item, index) => {
                                        return (
                                            <ConditionalWrapper
                                                condition={!!getPathname}
                                                wrapper={(children) => (
                                                    <Link
                                                        component={RouterLink}
                                                        display="flex"
                                                        underline="none"
                                                        color="figma.grayscale.tier3"
                                                        to={
                                                            getPathname
                                                                ? {
                                                                      pathname: getPathname(item),
                                                                      autoOpenData: {
                                                                          isAutoOpenPanel: true,
                                                                          autoOpenData: item,
                                                                          previousDataStack: [
                                                                              {
                                                                                  path: history.location.pathname,
                                                                              },
                                                                          ],
                                                                      },
                                                                  }
                                                                : null
                                                        }
                                                    >
                                                        {children}
                                                    </Link>
                                                )}
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    gap={1}
                                                    sx={{ overflow: 'hidden', minWidth: 0 }}
                                                >
                                                    <Typography
                                                        variant="16x400"
                                                        color="figma.grayscale.textHier2"
                                                        width="24px"
                                                        textAlign="center"
                                                        flexShrink={0}
                                                    >
                                                        {`${index + 1}.`}
                                                    </Typography>
                                                    <OverflowTooltipWrapper
                                                        placement="top-start"
                                                        arrow
                                                        title={
                                                            <Stack gap={1} overflow="scroll">
                                                                <Typography
                                                                    variant="16x400"
                                                                    color="figma.grayscale.textHier1"
                                                                    sx={{
                                                                        whiteSpace: 'pre-line',
                                                                        overflow: 'scroll',
                                                                        maxHeight: '50vh',
                                                                    }}
                                                                >
                                                                    {getName(item)}
                                                                </Typography>
                                                            </Stack>
                                                        }
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    backgroundColor: 'figma.grayscale.tier7',
                                                                    px: 2,
                                                                    py: 1,
                                                                    borderRadius: '6px',
                                                                    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                                                },
                                                            },
                                                            arrow: {
                                                                sx: {
                                                                    color: 'figma.grayscale.tier7',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="16x400"
                                                            color="figma.grayscale.textHier1"
                                                            noWrap
                                                            sx={
                                                                getPathname?.(item)
                                                                    ? {
                                                                          cursor: 'pointer',
                                                                          '&:hover': {
                                                                              color: 'figma.primary',
                                                                              textDecoration: 'underline',
                                                                          },
                                                                      }
                                                                    : {
                                                                          cursor: 'default',
                                                                      }
                                                            }
                                                        >
                                                            {toDisplayValue(getName(item))}
                                                        </Typography>
                                                    </OverflowTooltipWrapper>
                                                </Stack>
                                            </ConditionalWrapper>
                                        )
                                    })}
                                </Stack>
                            </Stack>
                        )
                    })}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default List
